import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { APIConstants } from "../../constants/ApiConstants";
import formatDate from "../../util/dateFormat";
import { useAppSelector } from "../../redux/hooks";
import theme from "../../theme";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import useButtonStyles from "../../styles/useButtonStyles";
import InfiniteTable from "../Table/InfiniteScroll/InfiniteTable";
import useGetTotalRecords from "../../Hooks/CustomHooks/UseGetTotalRecords";
import {
  SearchToolTip,
  filterConstants,
  DateConstants,
  permissionsConstants,
} from "../../constants/AppConstants";
import SearchInput from "../../Shared/Components/SearchInput/SearchInput";
import { usePermissionGetter } from "../../Hooks/Permissions/userPermissionGetter";
interface TableColumn {
  key: string;
  header: string;
  searchable?: boolean;
  filterable?: boolean;
}

const ActivePromotions: TableColumn[] = [
  { key: "ImagePath", header: " Image" },
  { key: "Title", header: "Title" },
  { key: "StartDate", header: "Start Date" },
  { key: "EndDate", header: "End Date" },
  { key: "IsFeatured", header: "Featured" },
  { key: "Status", header: "Status" },
  // { key: "CreateDate", header: "Creation Date" },
];

export const PromotionsMetaData = {
  ImagePath: {
    isImage: true,
    linkTo: "",
  },
  Title: {
    isImage: false,
    linkTo: "",
  },
  StartDate: {
    isImage: false,
    linkTo: "",
  },
  EndDate: {
    isImage: false,
    linkTo: "",
  },
  Status: {
    isImage: false,
    linkTo: "",
  },
  CreateDate: {
    isImage: false,
    linkTo: "",
  },
};
const AllPromotions = () => {
  const { getUserPermission: activePromotionsPermissions } =
    usePermissionGetter(
      permissionsConstants.promotions,
      permissionsConstants.active_promotions
    );
  const buttonStyles = useButtonStyles();
  const [openFilter, setOpenFilter] = useState(false);
  const [getActivePromotion, setActivePromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [filterActive, setFilterActive] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [creationDate, setCreationDate] = useState("");
  const [isResetApiCall, setIsResetApiCall] = useState(false);
  const { selectedMerchatDetails } = useAppSelector(
    (state: any) => state.persistReducers.getMerchantList
  );
  const { merchantId } = selectedMerchatDetails;
  const searchInputRef = useRef<HTMLInputElement>();
  const { totalCount, getTotalCounts } = useGetTotalRecords();
  const handleClose = () => {
    setOpenFilter(false);
  };

  const resetFields = () => {
    setCreationDate("");
    setEndDate("");
    setStartDate("");
    setFilterActive(false);
    setIsResetApiCall(true);
  };

  useEffect(() => {
    if (isResetApiCall) getPromotions();
  }, [isResetApiCall]);

  const getPromotions = async (searchValue?: string) => {
    let newSearchValue = encodeURIComponent(searchValue ? searchValue : "");
    try {
      setLoading(true);
      let url =
        process.env.REACT_APP_API_URL +
        APIConstants.getMerchantDeals +
        `?filter=promotions&MerchantId=${merchantId}${
          newSearchValue ? `&QuerySearch=${newSearchValue}` : ""
        }${startDate ? `&ValidityStartDate=${startDate}` : ""}${
          endDate ? `&ValidityEndDate=${endDate}` : ""
        }${creationDate ? `&CreationDate=${creationDate}` : ""}`;
      // if (getActivePromotion.length === 0) getTotalCounts(url, "data.Result");
      const promotions = await axios.get(url);
      let data = promotions.data.Result;

      let mappedData = data
        .filter((promo: any) => promo.Status === filterConstants.Active)
        .map((promotion: any) => {
          return {
            ImagePath: promotion?.ImagePath ? promotion?.ImagePath : "-",
            Title: promotion.Title ? promotion.Title : "-",
            StartDate: promotion?.StartDate
              ? formatDate(promotion.StartDate)
              : "-",
            EndDate: promotion?.EndDate ? formatDate(promotion.EndDate) : "-",
            Status: promotion?.Status ? promotion.Status : "-",
            CreateDate: promotion?.StartDate
              ? formatDate(promotion.StartDate)
              : "-",
            IsFeatured: promotion?.IsFeatured ? promotion.IsFeatured : "-",
          };
        });
      setActivePromotions(mappedData);
    } catch (error) {
      console.error(error);
      setLoading(false);
    } finally {
      setLoading(false);
      setIsResetApiCall(false);
    }
    if (creationDate || endDate || startDate) {
      setFilterActive(true);
    }
  };

  useEffect(() => {
    getPromotions();
  }, []);

  const handleSearch = () => {
    setActivePromotions([]);
    const searchValue = searchInputRef?.current?.value;
    getPromotions(searchValue ? searchValue : "");
  };

  return (
    <Grid>
      <Grid display={"flex"} justifyContent={"end"} px={4} gap={2} py={4}>
        <SearchInput
          HelpTip={SearchToolTip.Sales}
          onCallBack={handleSearch}
          ref={searchInputRef}
        />
        {activePromotionsPermissions("read") && (
          <Button
            variant="text"
            startIcon={
              filterActive ? (
                <FilterAltIcon sx={{ color: theme.color?.secondary }} />
              ) : (
                <FilterAltOutlinedIcon />
              )
            }
            sx={{
              color: theme.color?.newFilterGrey,
              textTransform: "capitalize",
            }}
            onClick={() => {
              setOpenFilter(true);
            }}
          >
            Filter
          </Button>
        )}

        <Dialog onClose={handleClose} open={openFilter} sx={{ ml: "60vw" }}>
          <Grid sx={{ width: "483px", py: 4, px: 2 }}>
            <DialogTitle
              display={"flex"}
              justifyContent={"space-between"}
              borderBottom={theme.border?.smallGrey}
            >
              <Typography
                sx={{
                  fontFamily: theme.font?.fontFamily.primary,
                  color: theme.color?.primary,
                  fontSize: theme.font?.fontSize.large,
                  fontWeight: theme.font?.fontWeight.thicker,
                }}
              >
                Filter
              </Typography>
              <IconButton onClick={() => setOpenFilter(false)}>
                <CloseOutlinedIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ borderBottom: theme.border?.smallGrey }}>
              <Grid display={"flex"} flexDirection={"column"} gap={1} pt={2}>
                <Grid pb={3} borderBottom={theme.border?.newGreyBorder}>
                  <Typography
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontSize: theme.font?.fontSize.medium,
                      fontWeight: theme.font?.fontWeight.thick,
                    }}
                  >
                    Start Date
                  </Typography>
                  <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                    <TextField
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      type="date"
                      size="small"
                      fullWidth
                      InputProps={{
                        inputProps: {
                          max: DateConstants.maxDate,
                        },
                      }}
                    ></TextField>
                  </Grid>
                </Grid>
                <Grid pb={3} borderBottom={theme.border?.newGreyBorder}>
                  <Typography
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontSize: theme.font?.fontSize.medium,
                      fontWeight: theme.font?.fontWeight.thick,
                    }}
                  >
                    End Date
                  </Typography>
                  <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                    <TextField
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      type="date"
                      size="small"
                      fullWidth
                      InputProps={{
                        inputProps: {
                          max: DateConstants.maxDate,
                        },
                      }}
                    ></TextField>
                  </Grid>
                </Grid>
                <Grid>
                  <Typography
                    sx={{
                      fontFamily: theme.font?.fontFamily.primary,
                      fontSize: theme.font?.fontSize.medium,
                      fontWeight: theme.font?.fontWeight.thick,
                    }}
                  >
                    Creation Date
                  </Typography>
                  <Grid display={"flex"} mt={2} gap={2} width={"70%"}>
                    <TextField
                      value={creationDate}
                      onChange={(e) => setCreationDate(e.target.value)}
                      type="date"
                      size="small"
                      fullWidth
                      InputProps={{
                        inputProps: {
                          max: DateConstants.maxDate,
                        },
                      }}
                    ></TextField>
                  </Grid>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ pr: 4, gap: 1, pt: 3 }}>
              <Button
                sx={{ ...buttonStyles.outlinedSecondary }}
                onClick={() => resetFields()}
              >
                Reset
              </Button>
              <Button
                sx={{ ...buttonStyles.containedSecondary }}
                onClick={() => {
                  setActivePromotions([]);
                  getPromotions();
                  setOpenFilter(false);
                }}
              >
                Apply
              </Button>
            </DialogActions>
          </Grid>
        </Dialog>
      </Grid>
      {activePromotionsPermissions("read") && (
        <InfiniteTable
          header={ActivePromotions}
          contentItems={getActivePromotion}
          metaData={PromotionsMetaData}
          hasMoreRecords={false}
          isInitialLoading={getActivePromotion.length === 0 && loading}
          totalRecords={getActivePromotion?.length}
        />
      )}
    </Grid>
  );
};

export default AllPromotions;
